import React, { useEffect, useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTransition, animated } from "react-spring";

import { setProfile } from "../../../features/ProfileSlice";
import {
  custProfile,
  custPromotion,
  custSurvey,
} from "../../../services/Api/Module/Customer";

import PageTitleMember from "../../../layouts/inc/PageTitleMember";
import CardPromotion from "../../../components/card/promotion/CardPromotion";
import MenuMember from "../../../components/menu/MenuMember";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";

const PULL_THRESHOLD = 60;
const MAX_PULL_DISTANCE = 80;

const MyPromotion = ({ articleItem }) => {
  let height = 0;

  const transitions = useTransition(
    articleItem.map((data) => ({
      ...data,
      y:
        (height += data.props.curHeight ? data.props.curHeight + 12 : 220) -
        (data.props.curHeight ? data.props.curHeight + 12 : 220),
    })),
    {
      key: (item) => item.key,
      from: { height: 0, opacity: 0, transform: "translateY(30px)" },
      enter: ({ y, height }) => ({
        y,
        height,
        opacity: 1,
        transform: "translateY(0)",
      }),
      leave: { height: 0, opacity: 0, transform: "translateY(30px)" },
      update: ({ data, y, height }) => ({ data, y, height }),
    }
  );

  return (
    <div
      className="item-group"
      style={{ position: "relative", height: height }}
    >
      {transitions((style, item, t, index) => (
        <animated.div
          className="item-promotion"
          style={{
            ...style,
            zIndex: articleItem.length - index,
            willChange: "transform, height, opacity",
          }}
        >
          {item}
        </animated.div>
      ))}
    </div>
  );
};

const Promotion = () => {
  const dispatch = useDispatch(); // ไว้อัพเดต state กลาง
  const stateProfile = useSelector((state) => state.profile);

  const setDataProfile = (
    id = 0,
    lineUid = null,
    custCode = null,
    branch = null,
    gender = null,
    dataUser = {},
    newSurvey = 0,
    newSurveyPoint = 0
  ) => {
    const tmpObj = {
      id: id,
      lineUid: lineUid,
      custCode: custCode,
      branch: branch,
      gender: gender,
      dataUser: dataUser,
      newSurvey: newSurvey,
      newSurveyPoint: newSurveyPoint,
    };
    dispatch(setProfile(tmpObj)); // setStateRedux ส่งค่าเก็บเข้า state กลาง
  };

  /* Handle goto page */
  const navigate = useNavigate();

  const handleGotoPage = (e) => {
    e.preventDefault();
    // console.log("click GotoPage", e.currentTarget.dataset.page);

    const page = e.currentTarget.dataset.page;
    navigate("/" + page);
  };
  /* End Handle goto page */

  /* First event render */
  const [isLoading, setIsLoading] = useState(true);

  const [initItem, setInitItem] = useState([]);
  const [initHeight, setInitHeight] = useState([]);
  const [curHeight, setCurHeight] = useState([]);

  const [promotionItem, setPromotionItem] = useState([]);
  const [articleItem, setArticleItem] = useState([]);

  /* Pull to refresh */
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const isAtTop = useRef(true);
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const listSectionRef = useRef(null);

  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  const handleRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    setIsRefreshing(true);
    setInitItem([]);
    setInitHeight([]);
    setCurHeight([]);
    setPromotionItem([]);
    setArticleItem([]);

    const _rsDataPromotion = await getCustPromotion({
      custId: stateProfile.id,
    });
    if (
      _rsDataPromotion.status === true &&
      _rsDataPromotion.result &&
      _rsDataPromotion.result.length !== 0
    ) {
      const _promotion = _rsDataPromotion.result;
      setInitItem(_promotion);
      setInitHeight(_promotion.map(() => 0));
    }
    setIsRefreshing(false);
    setPullDistance(0);
  }, [stateProfile.id]);

  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
    isAtTop.current =
      window.scrollY === 0 || document.documentElement.scrollTop === 0;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const currentPull = pullDistanceRef.current;
    if (currentPull >= PULL_THRESHOLD && !isRefreshingRef.current) {
      handleRefresh();
    } else {
      setPullDistance(0);
    }
  }, [handleRefresh]);

  /* touchmove ต้องใช้ addEventListener แบบ passive: false เพื่อให้ preventDefault() ทำงาน (แก้ error ใน console) */
  useEffect(() => {
    const el = listSectionRef.current;
    if (!el) return;

    const onTouchMove = (e) => {
      if (!isAtTop.current || isRefreshingRef.current) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (scrollTop > 5) {
        isAtTop.current = false;
        setPullDistance(0);
        return;
      }
      const currentY = e.touches[0].clientY;
      const diff = currentY - touchStartY.current;
      if (diff > 0) {
        e.preventDefault();
        setPullDistance(Math.min(diff * 0.5, MAX_PULL_DISTANCE));
      }
    };

    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, []);
  /* End Pull to refresh */

  const getCustProfile = async (payload) => {
    let _return = {
      status: false,
      result: null,
    };

    try {
      const { data } = await custProfile({ params: payload });
      if (data.resultCode === 20200) {
        const resData = data.body;
        // console.log("getCustProfile resData >>", resData);

        _return.status = true;
        _return.result = resData;
      }
    } catch (e) {
      console.log("ERR getCustProfile >> ", e);
    }

    return _return;
  };

  const getSurvey = async (payload) => {
    let _return = {
      status: false,
      result: null,
    };

    try {
      const { data } = await custSurvey({ params: payload });
      if (data.resultCode === 20200) {
        const resData = data.body;
        // console.log("getSurvey resData >>", resData);

        _return.status = true;
        _return.result = resData;
      }
    } catch (e) {
      console.log("ERR getSurvey >> ", e);
    }

    return _return;
  };

  const getCustPromotion = async (payload) => {
    let _return = {
      status: false,
      result: null,
    };

    try {
      const { data } = await custPromotion({ params: payload });
      if (data.resultCode === 20200) {
        const resData = data.body;
        // console.log("getCustPromotion resData >>", resData);

        _return.status = true;
        _return.result = resData;
      }
    } catch (e) {
      console.log("ERR getCustPromotion >> ", e);
    }

    return _return;
  };

  useEffect(() => {
    // console.log("useEffect !", stateProfile);

    if (stateProfile.lineUid === null) {
      navigate("/");
    } else {
      getCustProfile({}).then((_rsData) => {
        // console.log("_rsData >>", _rsData);

        if (_rsData.status === true && _rsData.result.length !== 0) {
          setDataProfile(
            _rsData.result.id,
            _rsData.result.lineUid,
            _rsData.result.custCode,
            _rsData.result.branch,
            _rsData.result.gender,
            {
              ...stateProfile.dataUser,
              ..._rsData.result,
            }
          );

          getSurvey({
            custId: _rsData.result.id,
            lineUid: _rsData.result.lineUid,
          }).then((_rsDataSurvey) => {
            // console.log("_rsDataSurvey >>", _rsDataSurvey);

            if (
              _rsDataSurvey.status === true &&
              _rsDataSurvey.result.length !== 0
            ) {
              setDataProfile(
                _rsData.result.id,
                _rsData.result.lineUid,
                _rsData.result.custCode,
                _rsData.result.branch,
                _rsData.result.gender,
                {
                  ...stateProfile.dataUser,
                  ..._rsData.result,
                },
                _rsDataSurvey.result.availableSurvey.toLocaleString(),
                _rsDataSurvey.result.totalPoint.toLocaleString()
              );

              getCustPromotion({
                custId: _rsData.result.id,
              }).then((_rsDataPromotion) => {
                // console.log("_rsDataPromotion >>", _rsDataPromotion);

                if (
                  _rsDataPromotion.status === true &&
                  _rsDataPromotion.result.length !== 0
                ) {
                  const _length = _rsDataPromotion.result.length;
                  const _promotion = _rsDataPromotion.result;
                  // console.log(_promotion);

                  for (let i = 0; i < _length; i++) {
                    setInitItem((prevInitItem) => [
                      ...prevInitItem,
                      _promotion[i],
                    ]);

                    setInitHeight((prevCurHeight) => [...prevCurHeight, 0]);
                  }
                }

                setIsLoading(false);
              });
            }
          });
        } else {
          navigate("/");
        }
      });
    }
  }, []);
  /* End First event render */

  /* Set new height on resize */
  let _tmpHeight = [];

  const setNewHeight = (index, height) => {
    // console.log(index, height);
    if (height !== 0) {
      _tmpHeight[index] = height;
      setCurHeight(_tmpHeight);
    }
  };
  /* End Set new height on resize */

  useEffect(() => {
    // console.log(initItem.length, initItem);

    if (initItem.length > 0) {
      for (let i = 0; i < initItem.length; i++) {
        setPromotionItem((prevPromotionItem) => [
          ...prevPromotionItem,
          <CardPromotion
            key={i + 1}
            index={i}
            setNewHeight={setNewHeight}
            curHeight={curHeight[i]}
            dataPromotion={initItem[i]}
          />,
        ]);

        _tmpHeight[i] = initHeight[i];
      }
    }
  }, [initItem]);

  useEffect(() => {
    if (promotionItem) {
      // console.log(promotionItem.length, promotionItem);

      setArticleItem(promotionItem);
    }
  }, [promotionItem]);

  useEffect(() => {
    // console.log(curHeight.length, curHeight);

    if (curHeight.length > 0) {
      curHeight.map((val, idx) => {
        setPromotionItem((prevPromotionItem) =>
          prevPromotionItem.with(
            idx,
            <CardPromotion
              key={promotionItem[idx].key}
              index={idx}
              setNewHeight={setNewHeight}
              curHeight={val}
              dataPromotion={promotionItem[idx].props.dataPromotion}
            />
          )
        );
      });
    }
  }, [curHeight]);

  useEffect(() => {
    // console.log(articleItem.length, articleItem);
  }, [isLoading, articleItem]);

  return (
    <>
      <Helmet>
        <title>Health Up</title>
        <meta name="title" content="Health Up" />
        <meta name="description" content="" />
        <meta name="keyword" content="" />
        <meta property="og:title" content="Health Up" />
        <meta property="og:description" content="" />
        <meta property="og:image" content="" />
        <meta property="og:url" content="" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Health Up" />
      </Helmet>

      <div id="pageCoupon-promotion">
        <main>
          <div className="wrapContent">
            <PageTitleMember title="โปรโมชั่น" />

            <section id="pSubmenuMain">
              <div className="selectMenu">
                <div
                  className="selectHeader"
                  onClick={handleGotoPage}
                  data-page="coupon"
                >
                  <div className="coupon">
                    <p>แลกคูปอง</p>
                    <div className="underline"></div>
                  </div>
                </div>
                <div
                  className="selectHeader active"
                  onClick={handleGotoPage}
                  data-page="promotion"
                >
                  <div className="promotion">
                    <p>โปรโมชั่น</p>
                    <div className="underline"></div>
                  </div>
                </div>
              </div>

              <div className="bgList">
                <section
                  ref={listSectionRef}
                  id="pListCouponMain"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  style={{ position: "relative" }}
                >
                  {/* Pull to refresh indicator */}
                  <div
                    className="pull-to-refresh-indicator"
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: 0,
                      height:
                        pullDistance || (isRefreshing ? PULL_THRESHOLD : 0),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: isRefreshing ? "height 0.2s" : "none",
                      zIndex: 10,
                      pointerEvents: "none",
                    }}
                  >
                    {(pullDistance > 0 || isRefreshing) && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        {isRefreshing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>กำลังโหลด...</span>
                          </>
                        ) : pullDistance >= PULL_THRESHOLD ? (
                          <>
                            <FontAwesomeIcon icon={faArrowsRotate} />
                            <span>ปล่อยเพื่อรีเฟรช</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={faArrowsRotate}
                              style={{
                                transform: `rotate(${
                                  (pullDistance / PULL_THRESHOLD) * 180
                                }deg)`,
                              }}
                            />
                            <span>ดึงลงเพื่อรีเฟรช</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className="bListCoupon"
                    style={{
                      transform:
                        pullDistance > 0
                          ? `translateY(${Math.min(
                              pullDistance,
                              MAX_PULL_DISTANCE
                            )}px)`
                          : undefined,
                      transition:
                        pullDistance === 0 ? "transform 0.2s" : "none",
                    }}
                  >
                    {isLoading ? (
                      <div className="bNoData">
                        <p>
                          <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                        </p>
                      </div>
                    ) : (
                      <>
                        {articleItem.length === 0 ? (
                          <div className="bNoData">
                            <p>{`ยังไม่มีโปรโมชั่นในขณะนี้`}</p>
                          </div>
                        ) : (
                          <div className="bGroup ">
                            <MyPromotion articleItem={articleItem} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </section>
              </div>
            </section>

            <MenuMember
              pageActive="promotion"
              newSurvey={stateProfile.newSurvey}
              newSurveyPoint={stateProfile.newSurveyPoint}
            />
          </div>
        </main>
      </div>
    </>
  );
};

export default Promotion;
